"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { usePathname } from "next/navigation";
import {
  CheckIcon,
  ChevronDownIcon,
  GlobeIcon,
  SearchIcon,
} from "lucide-react";
import {
  GOOGLE_TRANSLATE_LANGUAGES,
  TRANSLATE_AUTO_SOURCE,
  TRANSLATE_INTERACTIVE_PREFIXES,
  toGoogleTranslateCode,
} from "@tripcart/shared/apps/translate-langs";
import { toInternalPath } from "@tripcart/shared";
import { languageFlagCountry } from "@tripcart/shared/i18n/language-flag";
import { FlagIcon } from "@tripcart/traveler-ui/components/flag-icon";

import { usePermalinks } from "@/lib/permalinks-context";
import { useTranslator } from "@/lib/translation-context";

import styles from "./language-picker.module.css";

/**
 * Storefront language picker for the Google Translate app store app.
 *
 * Header control: a compact pill (flag + short code, e.g. 🇬🇧 EN) that opens a
 * searchable dropdown of every supported language, each row showing its flag,
 * native name, and English name. Picking a language drives Google Translate.
 *
 * Performance contract (apps/traveler-web/CLAUDE.md): a visitor who never
 * switches language downloads zero Google bytes. This control sets the
 * `googtrans` cookie and reloads; the inline bootstrap (from the app's
 * buildScripts) then injects Google's `element.js` only because the cookie is
 * now present. Picking the source language clears the cookie (back to original).
 *
 * Renders nothing on the interactive flows — those routes are never translated,
 * to keep checkout/booking fast and free of Google's DOM rewriting.
 */
type TranslateConfig = {
  autoDetect?: boolean;
  pageLanguage?: string;
};

declare global {
  interface Window {
    __tcGoogleTranslate?: TranslateConfig;
  }
}

function readCurrentTarget(): string | null {
  const match = document.cookie.match(/(?:^|;\s*)googtrans=([^;]+)/);
  if (!match?.[1]) return null;
  const parts = decodeURIComponent(match[1]).split("/");
  return parts[parts.length - 1] || null;
}

// Google's widget sets its own `googtrans` cookie on the registrable parent
// domain (e.g. `.tripcart.test`). A host/path-only write doesn't override it, so
// after the first translation the reload keeps reading Google's cookie and the
// language never changes. Write/clear every scope Google might use: host-only,
// the exact hostname, and each domain suffix with a leading dot — INCLUDING the
// full hostname (`i = 0`). On an apex-served storefront (`acme.com`) Google sets
// the cookie on `.acme.com`; starting the loop at `i = 1` skipped that leading-dot
// scope entirely, so the language got stuck. The TLD-only suffix is never reached
// (loop stops before `parts.length - 1`), so we never write a `.com`-wide cookie.
function googtransScopes(): Array<string | null> {
  const host = window.location.hostname;
  const scopes: Array<string | null> = [null, host];
  const parts = host.split(".");
  for (let i = 0; i < parts.length - 1; i += 1) {
    scopes.push(`.${parts.slice(i).join(".")}`);
  }
  return scopes;
}

function writeGoogtrans(value: string | null): void {
  const base = value
    ? `googtrans=${value};path=/;max-age=31536000`
    : "googtrans=;path=/;max-age=0";
  for (const scope of googtransScopes()) {
    document.cookie = scope ? `${base};domain=${scope}` : base;
  }
}

function switchLanguage(source: string, target: string): void {
  writeGoogtrans(target === source ? null : `/${source}/${target}`);
  window.location.reload();
}

// Auto-detect applies its choice via a reload (Google translates from the cookie
// on the next load). Guard it to ONE attempt per session so that if the cookie
// write doesn't stick (cookies blocked / private mode), we can't loop-reload
// forever. If sessionStorage itself is unavailable, skip the reload entirely.
function claimAutoDetectReload(): boolean {
  try {
    if (sessionStorage.getItem("tc-gt-autodetected")) return false;
    sessionStorage.setItem("tc-gt-autodetected", "1");
    return true;
  } catch {
    return false;
  }
}

function isExcluded(pathname: string): boolean {
  return TRANSLATE_INTERACTIVE_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
  );
}

/** On first visit, match the browser language if Google can translate to it.
 *  `googleSource` is null when the site language is one Google cannot translate
 *  from — auto-detect still works, it just has no source to compare against. */
function autoDetectTarget(
  currentTarget: string | null,
  googleSource: string | null,
): string | null {
  if (currentTarget) return null;
  const target = toGoogleTranslateCode(navigator.language);
  return target && target !== googleSource ? target : null;
}

// ── Presentation: language → flag + display code ─────────────────────────────

// The language → representative-country map lives in `@tripcart/shared`
// (`languageFlagCountry`) so this picker and operator-web's Translation
// settings cannot drift apart on which flag stands for which language. The
// flag is decorative — the native + English name always carry the meaning —
// and a language with no honest single-country flag resolves to undefined,
// which renders a globe here rather than an arbitrary country.

// Friendly two-letter codes for the pill where the Google code is a legacy or
// region tag that reads oddly uppercased (iw → HE, jw → JV, zh-* → ZH).
const SHORT_CODE_OVERRIDE: Record<string, string> = {
  iw: "HE",
  jw: "JV",
  "zh-CN": "ZH",
  "zh-TW": "ZH",
};

function shortCode(code: string): string {
  return (
    SHORT_CODE_OVERRIDE[code] ?? (code.split("-")[0] || code).toUpperCase()
  );
}

// Names resolved in English are SSR-safe (full ICU on Node + browser). Native
// names (autonyms) are computed lazily for the dropdown only — which never
// renders on the server — so Node's partial ICU can't cause a hydration mismatch.
const ENGLISH_NAMES = new Intl.DisplayNames(["en"], { type: "language" });

function englishName(code: string): string {
  try {
    return ENGLISH_NAMES.of(code) ?? code;
  } catch {
    return code;
  }
}

function nativeName(code: string): string {
  try {
    return new Intl.DisplayNames([code], { type: "language" }).of(code) ?? "";
  } catch {
    return "";
  }
}

interface LanguageOption {
  code: string;
  country: string | undefined;
  short: string;
  english: string;
  native: string;
}

// ── Placement ────────────────────────────────────────────────────────────────

/** The dialog's size in CSS px (`.dialog`: 72 × the 4px step wide; search row
 *  plus an 18-step list tall), for deciding where it fits. */
const DIALOG_WIDTH = 288;
const DIALOG_HEIGHT = 360;

interface DialogPlacement {
  /** Opens above the trigger when there is more room there than below. */
  side: "top" | "bottom";
  /** Lines up with the trigger's left edge when a right-aligned dialog would
   *  run off the left of the screen. */
  align: "start" | "end";
}

/**
 * Where the dialog fits around `trigger`. In the footer the picker sits at the
 * bottom of the page, so opening downward put most of the list below the fold
 * (TC-66 #1), and on a phone a dialog lined up with the trigger's right edge
 * ran off the left of the screen (TC-66 #31).
 */
function dialogPlacement(trigger: HTMLElement | null): DialogPlacement {
  if (!trigger) return { side: "bottom", align: "end" };
  const rect = trigger.getBoundingClientRect();
  const below = window.innerHeight - rect.bottom;
  const side = below < DIALOG_HEIGHT && rect.top > below ? "top" : "bottom";
  const align = rect.right < DIALOG_WIDTH ? "start" : "end";
  return { side, align };
}

// ── Component ────────────────────────────────────────────────────────────────

export function LanguagePicker({
  sourceLocale,
}: {
  sourceLocale: string;
}): React.ReactElement | null {
  // The ROUTE, not the address: a custom Traveler Login word (`/login/`) is
  // served by `/sign-in/`, which is what the excluded list names.
  const address = usePathname();
  const excluded = isExcluded(toInternalPath(usePermalinks(), address));
  const t = useTranslator();
  // The agency stores a WordPress locale (`ne_NP`); Google speaks `ne`. Every
  // use below — what Google is told the page is written in, which row is "your
  // language", and the source half of the cookie — needs the Google code, and
  // handing any of them the raw locale is what left Nepali listed twice with
  // neither row translating. Null means Google cannot translate this language:
  // there is then no source row to offer and Google detects the page itself.
  const googleSource = toGoogleTranslateCode(sourceLocale);
  const [current, setCurrent] = useState<string>(
    googleSource ?? TRANSLATE_AUTO_SOURCE,
  );
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState<DialogPlacement>({
    side: "bottom",
    align: "end",
  });
  const [query, setQuery] = useState("");
  const [highlight, setHighlight] = useState(0);

  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listId = useId();

  useEffect(() => {
    if (excluded) return;
    const config = window.__tcGoogleTranslate ?? {};
    // The init callback reads the source language from here before translating.
    config.pageLanguage = googleSource ?? TRANSLATE_AUTO_SOURCE;
    window.__tcGoogleTranslate = config;

    const target = readCurrentTarget();
    setCurrent(target ?? googleSource ?? TRANSLATE_AUTO_SOURCE);

    const detected = autoDetectTarget(target, googleSource);
    if (detected && claimAutoDetectReload()) {
      switchLanguage(googleSource ?? TRANSLATE_AUTO_SOURCE, detected);
    }
  }, [excluded, googleSource]);

  // Source language first, then the rest alphabetised by English name so the
  // long list scans naturally. Built once on the client (native autonyms need
  // browser ICU); the dropdown is the only consumer and never SSRs.
  const options = useMemo<LanguageOption[]>(() => {
    const describe = (code: string): LanguageOption => ({
      code,
      country: languageFlagCountry(code),
      short: shortCode(code),
      english: englishName(code),
      native: nativeName(code),
    });
    const rest = GOOGLE_TRANSLATE_LANGUAGES.filter((c) => c !== googleSource)
      .map(describe)
      .sort((a, b) => a.english.localeCompare(b.english));
    // No source row when Google cannot translate this site's language: offering
    // one would be a row that clears translation and does nothing else.
    return googleSource ? [describe(googleSource), ...rest] : rest;
  }, [googleSource]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (o) =>
        o.english.toLowerCase().includes(q) ||
        o.native.toLowerCase().includes(q) ||
        o.code.toLowerCase().includes(q) ||
        o.short.toLowerCase().includes(q),
    );
  }, [options, query]);

  // Open lifecycle: focus the search, close on outside-click / Escape, restore
  // focus to the trigger on close.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setHighlight(0);
    const id = requestAnimationFrame(() => searchRef.current?.focus());

    const onPointerDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      cancelAnimationFrame(id);
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  // Keep the highlighted row in view as the user arrows through results.
  useEffect(() => {
    if (!open) return;
    listRef.current
      ?.querySelector<HTMLElement>(`[data-idx="${highlight}"]`)
      ?.scrollIntoView({ block: "nearest" });
  }, [highlight, open]);

  if (excluded) return null;

  const choose = (code: string) =>
    switchLanguage(googleSource ?? TRANSLATE_AUTO_SOURCE, code);

  const onListNav = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const opt = filtered[highlight];
      if (opt) choose(opt.code);
    }
  };

  // The trigger must name the language the page is ACTUALLY in.
  //
  // `?? options[0]` was reachable and wrong: when Google cannot translate this
  // site's language there is deliberately no source row, `current` is the
  // auto-detect sentinel, and nothing in `options` matches it — so the trigger
  // labelled the page as whichever language sorts first in English (Afrikaans).
  //
  // The site's own locale is describable regardless of what Google offers: the
  // name helpers go through `Intl.DisplayNames`, which knows the tag even when
  // the translation list does not.
  const currentOption =
    options.find((o) => o.code === current) ??
    ({
      code: sourceLocale,
      country: languageFlagCountry(sourceLocale),
      short: shortCode(sourceLocale),
      english: englishName(sourceLocale),
      native: nativeName(sourceLocale),
    } satisfies LanguageOption);

  return (
    <div ref={rootRef} className={styles.root} translate="no">
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => {
          if (!open) setPlacement(dialogPlacement(triggerRef.current));
          setOpen(!open);
        }}
        className={styles.trigger}
      >
        {currentOption.country ? (
          <FlagIcon
            code={currentOption.country}
            eager
            className={styles.triggerFlag}
          />
        ) : (
          <GlobeIcon className={styles.globe} strokeWidth={1.75} />
        )}
        <span className={styles.short}>{currentOption.short}</span>
        {/* The purpose, out of sight. NOT an `aria-label` on the button: that
            replaced the name entirely, so the accessible name was "Change
            Language" while the visible text was "EN" — WCAG 2.5.3 Label in
            Name, and a voice user saying "click EN" got nothing. The name now
            reads "EN Change Language", which contains what is on screen. */}
        <span className="tui-sr-only">{t("Change Language")}</span>
        <ChevronDownIcon
          className={styles.chevron}
          strokeWidth={2}
          aria-hidden
        />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label={t("Select a Language")}
          className={styles.dialog}
          data-side={placement.side}
          data-align={placement.align}
        >
          <div className={styles.search}>
            <SearchIcon
              className={styles.searchIcon}
              strokeWidth={1.75}
              aria-hidden
            />
            <input
              ref={searchRef}
              type="text"
              role="combobox"
              aria-expanded
              aria-controls={listId}
              aria-activedescendant={
                filtered.length > 0 ? `${listId}-opt-${highlight}` : undefined
              }
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setHighlight(0);
              }}
              onKeyDown={onListNav}
              placeholder={t("Search Languages")}
              className={styles.input}
            />
          </div>

          <ul
            ref={listRef}
            id={listId}
            role="listbox"
            aria-label={t("Languages")}
            className={styles.list}
          >
            {filtered.length === 0 ? (
              <li className={styles.empty}>{t("No Languages Found")}</li>
            ) : (
              filtered.map((opt, i) => {
                const active = opt.code === current;
                return (
                  <li
                    key={opt.code}
                    id={`${listId}-opt-${i}`}
                    role="option"
                    aria-selected={active}
                  >
                    <button
                      type="button"
                      data-idx={i}
                      onClick={() => choose(opt.code)}
                      onMouseMove={() => setHighlight(i)}
                      data-highlighted={i === highlight ? "" : undefined}
                      data-active={active ? "" : undefined}
                      className={styles.option}
                    >
                      {opt.country ? (
                        <FlagIcon
                          code={opt.country}
                          className={styles.optionFlag}
                        />
                      ) : (
                        <GlobeIcon
                          className={styles.optionGlobe}
                          strokeWidth={1.5}
                          aria-hidden
                        />
                      )}
                      <span className={styles.names}>
                        <span className={styles.name}>
                          {opt.native || opt.english}
                        </span>
                        {opt.native && opt.native !== opt.english && (
                          <span className={styles.english}>{opt.english}</span>
                        )}
                      </span>
                      {opt.code === googleSource && (
                        <span className={styles.original}>{t("Original")}</span>
                      )}
                      {active && (
                        <CheckIcon
                          className={styles.check}
                          strokeWidth={2.25}
                          aria-hidden
                        />
                      )}
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}
